import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { getUsers, getPaymentMethods, createTransaction } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const CreateTransaction = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validated, setValidated] = useState(false);
    const [users, setUsers] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');

    const [formData, setFormData] = useState({
        type: '',
        amount: '',
        description: '',
        medium: '',
        transactionId: '',
        metadata: {
            reason: '',
            referenceId: '',
            bonusType: '',
            validityPeriod: '',
            bankReference: ''
        }
    });

    const transactionTypes = [
        { value: 'deposit', label: 'Deposit' },
        { value: 'withdrawal', label: 'Withdrawal' },
        { value: 'bonus', label: 'Bonus' }
    ];

    const mediumOptions = [
        { value: 'UPI', label: 'UPI' },
        { value: 'NEFT', label: 'NEFT' },
        { value: 'RTGS', label: 'RTGS' },
        { value: 'IMPS', label: 'IMPS' },
        { value: 'bank_transfer', label: 'Bank Transfer' }
    ];

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (selectedUserId && (formData.type === 'deposit' || formData.type === 'withdrawal')) {
            fetchPaymentMethods();
        } else {
            setPaymentMethods([]);
        }
    }, [selectedUserId, formData.type]);

    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await getUsers({ page: 1, limit: 1000 });
            const usersData = response.data?.users || response.data || [];
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to fetch users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoadingPaymentMethods(true);
            const response = await getPaymentMethods();
            const methodsData = response.data?.paymentMethods || response.data || [];
            setPaymentMethods(Array.isArray(methodsData) ? methodsData : []);
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setError('Failed to fetch payment methods');
        } finally {
            setLoadingPaymentMethods(false);
        }
    };

    const handleTypeChange = (e) => {
        const type = e.target.value;
        setFormData(prev => ({
            ...prev,
            type,
            medium: type === 'bonus' ? '' : prev.medium,
            metadata: {
                reason: '',
                referenceId: '',
                bonusType: '',
                validityPeriod: '',
                bankReference: ''
            }
        }));
        setSelectedPaymentMethodId('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('metadata.')) {
            const metadataField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    [metadataField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }
        
        try {
            setLoading(true);
            setError('');

            if (!selectedUserId) {
                throw new Error('Please select a user');
            }

            if ((formData.type === 'deposit' || formData.type === 'withdrawal') && !selectedPaymentMethodId) {
                throw new Error('Please select a payment method');
            }

            const payload = {
                userId: selectedUserId,
                type: formData.type,
                amount: parseFloat(formData.amount),
                description: formData.description,
                metadata: {}
            };

            // Add payment method and medium for deposit/withdrawal
            if (formData.type === 'deposit' || formData.type === 'withdrawal') {
                payload.paymentMethodId = selectedPaymentMethodId;
                payload.medium = formData.medium;
                payload.transactionId = formData.transactionId;
                
                if (formData.type === 'deposit') {
                    payload.metadata = {
                        reason: formData.metadata.reason,
                        referenceId: formData.metadata.referenceId
                    };
                } else {
                    payload.metadata = {
                        reason: formData.metadata.reason,
                        bankReference: formData.metadata.bankReference
                    };
                }
            }

            // Add bonus specific fields
            if (formData.type === 'bonus') {
                payload.metadata = {
                    bonusType: formData.metadata.bonusType,
                    validityPeriod: formData.metadata.validityPeriod
                };
            }

            const response = await createTransaction(payload);

            if (response.success) {
                alert('Transaction created successfully');
                navigate(`/admin/transaction/${response.data?.transaction?._id || response.data?._id}`);
            } else {
                throw new Error(response.message || 'Failed to create transaction');
            }
        } catch (error) {
            console.error('Error creating transaction:', error);
            setError(error.message || 'Failed to create transaction');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Create Transaction" sidebutton={true} />

            <div className="row">
                <div className="col-xl-8 col-lg-10 mx-auto">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom py-3">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-plus-circle text-primary me-2" style={{ fontSize: '20px' }}></i>
                                <Card.Title className="mb-0 fw-bold">Create Manual Transaction</Card.Title>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {error && (
                                <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                User <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedUserId}
                                                onChange={(e) => setSelectedUserId(e.target.value)}
                                                required
                                                disabled={loadingUsers}
                                                className="form-control-lg"
                                            >
                                                <option value="">{loadingUsers ? 'Loading users...' : 'Select User'}</option>
                                                {users.map((user) => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name} ({user.email}) - {user.mobileNumber}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select a user
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Transaction Type <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleTypeChange}
                                                required
                                                className="form-control-lg"
                                            >
                                                <option value="">Select Type</option>
                                                {transactionTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select transaction type
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {(formData.type === 'deposit' || formData.type === 'withdrawal') && (
                                        <>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Payment Method <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={selectedPaymentMethodId}
                                                        onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                                                        required
                                                        disabled={loadingPaymentMethods || !selectedUserId}
                                                        className="form-control-lg"
                                                    >
                                                        <option value="">
                                                            {loadingPaymentMethods ? 'Loading payment methods...' : 'Select Payment Method'}
                                                        </option>
                                                        {paymentMethods.map((method) => (
                                                            <option key={method._id} value={method._id}>
                                                                {method.bankName} - {method.accountNumber} - {method.ifscCode} ({method.accountHolderName})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                        Please select a payment method
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Medium <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        name="medium"
                                                        value={formData.medium}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="form-control-lg"
                                                    >
                                                        <option value="">Select Medium</option>
                                                        {mediumOptions.map(medium => (
                                                            <option key={medium.value} value={medium.value}>
                                                                {medium.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                        Please select medium
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Amount <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                placeholder="Enter amount"
                                                required
                                                min="0"
                                                step="0.01"
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid amount
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Description <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Enter description"
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter description
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {formData.type === 'deposit' && (
                                        <>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Transaction ID <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="transactionId"
                                                        value={formData.transactionId}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter transaction ID"
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please enter transaction ID
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Reference ID
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="metadata.referenceId"
                                                        value={formData.metadata.referenceId}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter reference ID"
                                                        className="form-control-lg"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Reason <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="metadata.reason"
                                                        value={formData.metadata.reason}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter reason"
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please enter reason
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}

                                    {formData.type === 'withdrawal' && (
                                        <>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Bank Reference <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="metadata.bankReference"
                                                        value={formData.metadata.bankReference}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter bank reference"
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please enter bank reference
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Reason <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="metadata.reason"
                                                        value={formData.metadata.reason}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter reason"
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please enter reason
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}

                                    {formData.type === 'bonus' && (
                                        <>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Bonus Type <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="metadata.bonusType"
                                                        value={formData.metadata.bonusType}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter bonus type"
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please enter bonus type
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold mb-2">
                                                        Validity Period <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="month"
                                                        name="metadata.validityPeriod"
                                                        value={formData.metadata.validityPeriod}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="form-control-lg"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please select validity period
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/admin/transactions')}
                                        className="px-4"
                                    >
                                        <i className="fa fa-times me-2"></i> Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                        className="px-4"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-plus me-2"></i> Create Transaction
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
            
            <style>{`
                .form-control-lg:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }
                .card {
                    transition: transform 0.2s ease-in-out;
                }
                .form-label {
                    color: #495057;
                }
                .text-danger {
                    font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default CreateTransaction;

